import type { MouseEvent } from 'react';
import type { OrderError, OrderIssueCode } from '@skupreme/api-contracts/core';

/* The reason string the pipeline writes for an address it could not use. */
export const INVALID_ADDRESS = 'Invalid Address';

/* The process the label step stamps on the order_error it writes. */
export const LABEL_GENERATION = 'labelGeneration';

/* Mostly from the API's `issue_code`; read from the reason's words only for rows without one. */
export type OrderIssueKind =
  | 'address'
  | 'inventory'
  | 'provider'
  | 'label'
  | 'shipment'
  | 'risk'
  | 'unknown';

/* Blocking means nothing moves until someone acts; warning means waiting. */
export type OrderIssueSeverity = 'blocking' | 'warning';

const WAITING_KINDS: OrderIssueKind[] = ['inventory', 'provider'];

export function getIssueSeverity(
  kind: OrderIssueKind = 'unknown',
): OrderIssueSeverity {
  return WAITING_KINDS.includes(kind) ? 'warning' : 'blocking';
}

export type OrderIssue = {
  key: string;
  reason: string;
  detail?: string;
  kind?: OrderIssueKind;
  /** The API's code for it; absent on rows and screens that do not send one. */
  code?: OrderIssueCode;
  /** Which step recorded it, e.g. `labelGeneration`. */
  process?: string;
  /** Set only for reasons the operator can act on from the list. */
  onFix?: (event: MouseEvent) => void;
  /** Replaces the recorded reason when the screen can say it better. */
  label?: string;
  /** What the operator does about it, where the screen knows. */
  hint?: string;
  /** EN-4544: the line SKU to look up in Catalog > Listings, when mapping a listing fixes it. */
  mapSku?: string;
};

/*
 * Where the API names the issue. `unmatched_inventory` stays `unknown` until the
 * badge has a look of its own: nothing ships until someone links the item, so it
 * must stay blocking, which no other kind is.
 */
const CODE_KINDS: Record<Exclude<OrderIssueCode, 'other'>, OrderIssueKind> = {
  out_of_stock: 'inventory',
  partial_oos: 'inventory',
  address_invalid: 'address',
  unmatched_inventory: 'unknown',
  no_provider: 'provider',
};

/* `other` says only which step failed: the label step, a carrier, or anything else. */
const PROCESS_KINDS: Record<string, OrderIssueKind> = {
  [LABEL_GENERATION]: 'label',
  Shipping: 'shipment',
};

/*
 * Fallback for errors without `issue_code`: the order details endpoint never
 * sends one, and list rows lack it until the API that adds it is deployed.
 */
const KIND_WORDS: [OrderIssueKind, RegExp][] = [
  ['address', /address|postal|zip|recipient/i],
  ['inventory', /inventory|stock|oos|quantity/i],
  ['provider', /provider|warehouse|fulfil?lment cent/i],
  ['label', /label|print|barcode/i],
  ['shipment', /ship|carrier|tracking|delivery/i],
  ['risk', /risk|fraud|hold/i],
];

export function getIssueKind(
  reason: string,
  process?: string,
  code?: OrderIssueCode,
): OrderIssueKind {
  if (code && code !== 'other') {
    return CODE_KINDS[code] ?? 'unknown';
  }

  if (code === 'other') {
    return (process && PROCESS_KINDS[process]) || 'unknown';
  }

  if (process === LABEL_GENERATION) {
    return 'label';
  }

  for (const [kind, words] of KIND_WORDS) {
    if (words.test(reason)) {
      return kind;
    }
  }

  return 'unknown';
}

/** `labelGeneration` as "Label generation". */
export function describeProcess(process: string | undefined): string {
  if (!process) {
    return '';
  }

  const words = process
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();

  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/**
 * The wire type promises an array, but a legacy order can still carry a bare
 * order_error object instead of one -- normalize once here so every reader
 * downstream can trust `.find`/`.filter` without re-guarding.
 */
function asErrorArray(
  errors: RecordedError[] | RecordedError | undefined,
): RecordedError[] {
  if (!errors) return [];
  return Array.isArray(errors) ? errors : [errors];
}

/*
 * EN-4497: the API names the line, its SKU, the provider and why for a line with
 * no provider SKU. It is not in the shared order_error type yet.
 */
export type ProviderSkuHold = {
  order_line_id?: string;
  line_sku?: string;
  provider_label: string;
  reason?: string;
};

export type RecordedError = OrderError & {
  provider_sku_hold?: ProviderSkuHold;
};

/* What follows "has no <provider> SKU: ", by the worker's reason. */
const HOLD_WHY: Record<string, (provider: string, skus: string) => string> = {
  no_listing_at_provider: () => 'its catalog item is not mapped yet',
  several_skus_no_stock: (provider, skus) =>
    `${skus} ${provider} SKUs and none has stock`,
  several_skus_unknown_stock: (provider, skus) =>
    `${skus} ${provider} SKUs and no stock reported to choose one`,
  no_fba_sku: () => 'its catalog item has no Fulfilled-by-Amazon listing',
  ambiguous_fba_sku: () =>
    'its catalog item has more than one Fulfilled-by-Amazon listing',
  bundle_component_unmapped: provider =>
    `a component of this bundle has no ${provider} listing`,
};

/* EN-4544: what to do, by the worker's reason; a reason not listed falls back to the no_provider_sku hint. */
const HOLD_HINTS: Record<string, string> = {
  no_listing_at_provider:
    'Map the catalog item to its listing at the provider. If the provider has no such product yet, add it there first.',
  several_skus_no_stock:
    'Add stock at the provider, or send the order from another warehouse.',
  several_skus_unknown_stock:
    'Wait for the provider to report stock, or send the order from another warehouse.',
  no_fba_sku:
    'Map the catalog item to its Fulfilled-by-Amazon listing, or send the order from another warehouse.',
  ambiguous_fba_sku:
    'Keep one Fulfilled-by-Amazon listing mapped to the catalog item.',
  bundle_component_unmapped:
    'Map each component of the bundle to its listing at the provider.',
};

/* The reasons a mapping in Catalog > Listings fixes; an unknown reason is offered it too, as the likeliest fix. */
const MAPPING_FIXES: ReadonlySet<string> = new Set([
  'no_listing_at_provider',
  'no_fba_sku',
  'bundle_component_unmapped',
]);

/** What to do about a line with no provider SKU, and the SKU to map when a mapping fixes it. */
function providerSkuHoldFix(
  error: RecordedError,
): Pick<OrderIssue, 'hint' | 'mapSku'> {
  const hold = error.provider_sku_hold;

  if (error.reason !== 'no_provider_sku' || !hold) {
    return {};
  }

  const known = !!hold.reason && Object.hasOwn(HOLD_HINTS, hold.reason);
  const mapsIt = !known || MAPPING_FIXES.has(hold.reason ?? '');

  return {
    ...(known ? { hint: HOLD_HINTS[hold.reason as string] } : {}),
    ...(mapsIt && hold.line_sku ? { mapSku: hold.line_sku } : {}),
  };
}

/* The candidate SKUs are only in the exception detail, which arrives as JSON text. */
function countCandidates(errorDetails: string | undefined): number | undefined {
  try {
    const { candidates } = JSON.parse(errorDetails ?? '{}');

    return Array.isArray(candidates) && candidates.length > 0
      ? candidates.length
      : undefined;
  } catch {
    return undefined;
  }
}

/** The sentence for a line with no provider SKU; undefined when the API did not name the line's SKU. */
function providerSkuHoldLabel(error: RecordedError): string | undefined {
  const hold = error.provider_sku_hold;

  if (error.reason !== 'no_provider_sku' || !hold?.line_sku) {
    return undefined;
  }

  const why =
    hold.reason && Object.hasOwn(HOLD_WHY, hold.reason)
      ? HOLD_WHY[hold.reason]
      : undefined;

  if (!why) {
    return `${hold.line_sku} has no SKU at ${hold.provider_label}`;
  }

  const skus = String(countCandidates(error.error_details) ?? 'several');

  return `${hold.line_sku} has no ${hold.provider_label} SKU: ${why(hold.provider_label, skus)}`;
}

/**
 * The unresolved reasons an order is waiting. Identical reasons recorded by
 * several processes collapse into one entry, keeping the copy with a detail.
 */
export function getOrderIssues(
  errors: RecordedError[] | undefined,
  isShipped?: boolean,
): OrderIssue[] {
  if (isShipped) {
    return [];
  }

  const byReason = new Map<string, OrderIssue>();

  for (const error of asErrorArray(errors)) {
    if (error.resolved || !error.reason) {
      continue;
    }

    const existing = byReason.get(error.reason);

    if (!existing) {
      byReason.set(error.reason, {
        key: error.reason,
        reason: error.reason,
        detail: error.error_details,
        label: providerSkuHoldLabel(error),
        ...providerSkuHoldFix(error),
        kind: getIssueKind(error.reason, error.process, error.issue_code),
        code: error.issue_code,
        process: error.process,
      });
      continue;
    }

    if (!existing.detail && error.error_details) {
      existing.detail = error.error_details;
    }

    existing.label ??= providerSkuHoldLabel(error);
    if (!existing.hint && !existing.mapSku) {
      Object.assign(existing, providerSkuHoldFix(error));
    }

    /* The same words from another step can carry the specific code the first copy lacked. */
    if (
      (!existing.code || existing.code === 'other') &&
      error.issue_code &&
      error.issue_code !== 'other'
    ) {
      existing.code = error.issue_code;
      existing.kind = getIssueKind(
        error.reason,
        error.process,
        error.issue_code,
      );
    }
  }

  return [...byReason.values()].map(withCodeLabel);
}

/*
 * V2 orders (orders-db order_exceptions) send the bare reason code, e.g.
 * `no_catalog_item`. A code is shown as words; the code stays in the tooltip.
 */
const MACHINE_CODE = /^[a-z0-9]+(_[a-z0-9]+)*$/;

/** A V2 reason code rather than prose the pipeline wrote. */
export const isMachineCode = (reason: string) => MACHINE_CODE.test(reason);

const CODE_LABELS: Record<string, string> = {
  no_catalog_item: 'No catalog item for a line',
  no_provider_sku: 'No provider SKU for a line',
  held_for_operator: 'Waiting for an operator',
  planner_unavailable: 'Routing is unavailable',
  routing_paused: 'Routing is paused',
  /* orders-db: the marketplace sent a line with no units on an order it did not cancel. */
  channel_quantity_zero: 'A line came with quantity 0',
};

const CANCEL_UNCONFIRMED = 'The provider did not confirm the cancel';

/*
 * EN-4544: the codes per exception kind: why a send to a provider failed or is
 * held (the orders worker's send_rejected / provider_refused / live_send_gated
 * codes), and who cancelled an order a provider already had
 * (cancel_after_handoff: cancel.ts, buyerCancelActivities.ts). The same code
 * can mean something else under another kind.
 */
const SEND_CODE_LABELS: Record<string, Record<string, string>> = {
  cancel_after_handoff: {
    channel: 'Marketplace cancelled after the provider took it',
    merchant: 'Cancelled in the store after the provider took it',
    operator: 'Cancelled after the provider took it',
    system: 'Cancelled after the provider took it',
    buyer_cancel_partial:
      'Buyer cancelled part of it after the provider took it',
    buyer_cancel_after_shipment: 'Buyer cancelled after it shipped',
    provider_cancel_unanswered: CANCEL_UNCONFIRMED,
    provider_cancel_not_configured: CANCEL_UNCONFIRMED,
    provider_cancel_no_node: CANCEL_UNCONFIRMED,
    provider_cancel_failed: CANCEL_UNCONFIRMED,
  },
  send_rejected: {
    connections_rejected: 'The provider rejected the order',
    not_sent_retries_exhausted: 'The order could not reach the provider',
    ship_to_incomplete: 'The ship-to address is incomplete',
    cancelled_at_provider: 'The provider canceled the order',
    mcf_order_exists: 'Amazon already has this order from another app',
  },
  provider_refused: {
    no_alias: 'The provider does not know a SKU of this order',
    no_stock: 'The provider has no stock for this order',
    bwp: 'The provider does not take Buy with Prime orders',
    unmapped: 'A line has no provider SKU',
    unfulfillable: 'The provider cannot fulfill this order',
    address: 'The provider refused the ship-to address',
    invalid: 'The provider found the order invalid',
    other: 'The provider refused the order',
    partial_rejection: 'The provider refused part of the order',
    rejected_after_accept: 'The provider refused the order after accepting it',
  },
  live_send_gated: {
    gated: 'Not sent',
    not_configured: 'Live sending is not set up',
    provider_type_unknown: 'This provider cannot take orders from here yet',
    invalid_request: 'The order could not be prepared for the provider',
    unknown_connection: 'The provider connection could not be reached',
    no_provider_node: 'No provider warehouse is chosen',
    rendered_not_sent: 'The order was prepared but not sent',
    legacy_lookup_unavailable: 'Held: the legacy check could not run',
    legacy_already_sent: 'Held: the legacy system may have sent it',
    connections_fault: 'The provider connection failed',
    replan_requires_operator: 'Routing changed after a send: confirm it',
  },
};

/* EN-4544: what to do, by reason code, else by exception kind; the fixes live on the order's Shipping tab. */
const ISSUE_HINTS: Record<string, string> = {
  no_catalog_item: 'Open the order and match the line to a catalog item.',
  no_provider_sku:
    'Map the catalog item to its listing at the provider, or send the order from another warehouse.',
  held_for_operator:
    'The rules found no warehouse for it. Open the order to see why.',
  cancel_after_handoff:
    'Cancel it at the provider, then confirm it on the order.',
  channel_quantity_zero: 'Check this order in the marketplace.',
  mcf_order_exists:
    'Another app or integration is already fulfilling it through Amazon. Do not send it again.',
};

const hintOf = (issue: OrderIssue) =>
  (Object.hasOwn(ISSUE_HINTS, issue.reason)
    ? ISSUE_HINTS[issue.reason]
    : undefined) ??
  (issue.process && Object.hasOwn(ISSUE_HINTS, issue.process)
    ? ISSUE_HINTS[issue.process]
    : undefined);

function withCodeLabel(issue: OrderIssue): OrderIssue {
  if (!MACHINE_CODE.test(issue.reason)) {
    return issue;
  }

  const hint = issue.hint ?? hintOf(issue);
  if (issue.label) {
    return hint ? { ...issue, hint } : issue;
  }

  const words = issue.reason.replace(/_+/g, ' ');
  const sendLabels: Record<string, string> =
    issue.process && Object.hasOwn(SEND_CODE_LABELS, issue.process)
      ? SEND_CODE_LABELS[issue.process]
      : {};
  const label =
    (Object.hasOwn(sendLabels, issue.reason)
      ? sendLabels[issue.reason]
      : undefined) ??
    CODE_LABELS[issue.reason] ??
    words.charAt(0).toUpperCase() + words.slice(1);

  return { ...issue, label, ...(hint ? { hint } : {}) };
}

/** The unresolved label entry, of which the step keeps at most one. */
export function getLabelIssue(
  errors: OrderError[] | undefined,
): OrderError | undefined {
  return asErrorArray(errors).find(
    error => error.process === LABEL_GENERATION && !error.resolved,
  );
}

/* The reason the label step writes when items have no package attributes. */
const MISSING_DIMENSION_PREFIX =
  'Items need dimensions for automatic label purchase:';

/**
 * The SKUs named in the label reason, which is the only place they reach the
 * client. The step lists at most five and collapses the rest into `+N more`,
 * so the overflow count comes back separately. Anchored to that exact copy:
 * the reason for an order blocked only on quantity names no SKU at all.
 */
export function getMissingDimensionSkus(reason: string | undefined): {
  skus: string[];
  overflow: number;
} {
  if (!reason?.startsWith(MISSING_DIMENSION_PREFIX)) {
    return { skus: [], overflow: 0 };
  }

  const listed = reason.slice(MISSING_DIMENSION_PREFIX.length).trim();
  const overflow = /\+(\d+)\s+more$/.exec(listed);
  const named = overflow ? listed.slice(0, overflow.index) : listed;

  return {
    skus: named
      .split(',')
      .map(sku => sku.trim())
      .filter(Boolean),
    overflow: overflow ? Number(overflow[1]) : 0,
  };
}

/** Puts the address issue first and gives it the handler, adding it if absent. */
export function withAddressFix(
  issues: OrderIssue[],
  onFix: (event: MouseEvent) => void,
): OrderIssue[] {
  const index = issues.findIndex(issue => issue.reason === INVALID_ADDRESS);

  if (index === -1) {
    return [
      {
        key: INVALID_ADDRESS,
        reason: INVALID_ADDRESS,
        kind: 'address',
        code: 'address_invalid',
        onFix,
      },
      ...issues,
    ];
  }

  const fixable = { ...issues[index], onFix };

  return [fixable, ...issues.slice(0, index), ...issues.slice(index + 1)];
}
